/**
 * Frontend overlay for known authored content (seed / catalog / plan copy).
 * Does not invent metrics — only localizes fixed English when present.
 * Future products should store localized fields; until then, unknown copy passes through.
 */

type Translate = (
  key: string,
  fallbackOrParams?: string | Record<string, string | number>,
  params?: Record<string, string | number>
) => string;

const KNOWN_COPY: Record<string, string> = {
  'Private client for BeCoder software distribution': 'content.seed.studio.tagline',
  'BeCoder Studio is our flagship application for licensed access across Web/PWA, Windows, Android APK, and iOS/PWA.':
    'content.seed.studio.description',
  'Initial private release of BeCoder Studio for Windows x64.': 'content.seed.studio.changelog',
  'Initial private release of BeCoder Studio for Windows x64': 'content.seed.studio.changelog',

  // Product taglines
  'Calm point of sale for modern counters.': 'content.seed.glasspos.tagline',
  'Books that stay quiet and correct.': 'content.seed.ledger.tagline',
  'Exchange operations with vault discipline.': 'content.seed.befx.tagline',
  'Commerce catalog built for regional retail.': 'content.seed.iqStore.tagline',
  'Live sports pulse for fans.': 'content.seed.aura.tagline',

  // Product descriptions (seed + staging)
  'GlassPOS is BeCoder’s point-of-sale application — fast checkout, inventory clarity, and offline-ready registers.':
    'content.seed.glasspos.description',
  "GlassPOS is BeCoder's point-of-sale application — fast checkout, inventory clarity, and offline-ready registers.":
    'content.seed.glasspos.description',
  'GlassPOS is BeCoder’s point-of-sale application.': 'content.seed.glasspos.descriptionShort',
  "GlassPOS is BeCoder's point-of-sale application.": 'content.seed.glasspos.descriptionShort',
  'Ledger brings double-entry finance into a calm workspace — parties, periods, and statements without noise.':
    'content.seed.ledger.description',
  'Ledger brings double-entry finance into a calm workspace.': 'content.seed.ledger.descriptionShort',
  'BeFX handles buy/sell, vaults, and remittances with audit-friendly daily close for exchange desks.':
    'content.seed.befx.description',
  'BeFX handles buy/sell, vaults, and remittances.': 'content.seed.befx.descriptionShort',
  'IQ Store manages products, orders, and store operations with an image-ready catalog experience.':
    'content.seed.iqStore.description',
  'IQ Store manages products, orders, and store operations.': 'content.seed.iqStore.descriptionShort',
  'Aura Sport delivers match energy with calm UI — scores, alerts, and mobile-first sports atmosphere.':
    'content.seed.aura.description',

  // Billing periods
  month: 'plan.period.month',
  year: 'plan.period.year',
  lifetime: 'plan.period.lifetime',
  Month: 'plan.period.month',
  Year: 'plan.period.year',
  Lifetime: 'plan.period.lifetime',

  // Plan names
  Starter: 'plan.name.starter',
  Growth: 'plan.name.growth',
  Enterprise: 'plan.name.enterprise',
  Monthly: 'plan.name.monthly',
  Annual: 'plan.name.annual',
  'Lifetime License': 'plan.name.lifetime',

  // Commercial plan features (seed)
  'Recurring monthly license': 'commercial.plan.monthly.f1',
  'Full core product use while active': 'commercial.plan.monthly.f2',
  'Product updates, bug & security fixes': 'commercial.plan.monthly.f3',
  'Technical support while subscription is active': 'commercial.plan.monthly.f4',
  'Same core benefits as Monthly': 'commercial.plan.annual.f1',
  '12 months billed once — about 17% savings': 'commercial.plan.annual.f2',
  'Pay once. Use forever.': 'commercial.plan.lifetime.f1',
  'Permanent right to use the licensed product': 'commercial.plan.lifetime.f2',
  'No mandatory monthly or annual renewal': 'commercial.plan.lifetime.f3',
  '3 months of Premium Technical Support included': 'commercial.plan.lifetime.f4',
  'Paid support after 3 months is optional — license stays valid': 'commercial.plan.lifetime.f5',

  // Product display / new commercial seed strings
  Ledger: 'product.display.raqib',
  Raqib: 'product.display.raqib',
  Raqb: 'product.display.raqib',
  BePharma: 'product.display.bepharma',
  'Cloud Services': 'commercial.plan.cloud_annual',
  Pharmacy: 'plan.category.pharmacy',
  'Modern point of sale for everyday selling.': 'commercial.glasspos.tagline',
  'Deeper store and inventory control.': 'commercial.iqStore.tagline',
  'Monitor your business and financial activity.': 'commercial.raqib.tagline',
  'Sell faster, monitor stock, and understand your daily sales.': 'commercial.glasspos.tagline',
  'Organize your products, inventory, and store from one place.': 'commercial.iqStore.tagline',
  'Monitor your money movement, receivables, expenses, and income with clarity.':
    'commercial.raqib.tagline',
  'Specialized management for exchange operations, currencies, and vaults.':
    'commercial.befx.tagline',
  'A modern point-of-sale system for managing sales, products, inventory, customers and day-to-day business reporting. Designed for shops and retail points that need fast, simple daily selling.':
    'commercial.glasspos.description',
  'A broader store and inventory management solution for businesses that need deeper control over products, stock, purchasing, sales and inventory movement.':
    'commercial.iqStore.description',
  'Raqib helps you monitor business accounts and financial activity from one place — money coming in and going out, account activity, and a clearer view of your overall financial position. It is a business monitoring product, not certified accounting or tax software.':
    'commercial.raqib.description',
  'A specialized system for exchange offices and currency-exchange businesses, designed to manage currency buying and selling, exchange rates, vault and cash movements, and financial operations in an organized environment.':
    'commercial.befx.description',
  'GlassPOS is a fast, practical Point of Sale system for businesses with frequent daily counter sales. It focuses on quick checkout, barcode sales, products, stock visibility, customers, and daily sales reporting — for shops where speed of selling is the priority.':
    'commercial.glasspos.description',
  'IQ Store is for businesses where managing products, inventory, catalog structure, and store operations matters more than simply fast checkout. It helps you organize complex catalogs and inventory-heavy retail from one place — not another version of the same POS.':
    'commercial.iqStore.description',
  'Raqib (راقب) helps business owners watch and understand the financial movement of their business — money in and out, income, expenses, receivables, financial records, and clearer visibility of what the business owns and owes. It is financial monitoring and business oversight, not a cashier or POS, and not certified accounting or tax software.':
    'commercial.raqib.description',
  'BeFX is specialized software for exchange offices and currency-exchange businesses. It focuses on currency buying and selling, exchange rates, vault and cash movement for exchange-office operations — not generic financial monitoring.':
    'commercial.befx.description',

  // Plan features (exact seed / enrichment strings)
  'Core access': 'plan.feature.coreAccessShort',
  'Email support': 'plan.feature.emailSupport',
  '1 organization': 'plan.feature.oneOrg',
  'organization 1': 'plan.feature.oneOrg',
  'Everything in Starter': 'plan.feature.everythingStarter',
  'Priority support': 'plan.feature.prioritySupport',
  'More seats': 'plan.feature.moreSeats',
  'Advanced reports': 'plan.feature.advancedReports',
  'Custom terms': 'plan.feature.customTerms',
  'Dedicated success': 'plan.feature.dedicatedSuccess',
  'SSO-ready path': 'plan.feature.ssoReady',
  'SLA options': 'plan.feature.slaOptions',

  // Showcase / enrichment feature bullets
  'Fast checkout with offline resilience': 'plan.feature.fastCheckout',
  'Inventory and multi-register sync': 'plan.feature.inventorySync',
  'Receipts, shifts, and staff roles': 'plan.feature.receiptsShifts',
  'Works on tablet and desktop': 'plan.feature.tabletDesktop',
  'Double-entry clarity without clutter': 'plan.feature.doubleEntryClarity',
  'Books, parties, and period close': 'plan.feature.booksParties',
  'Export-ready statements': 'plan.feature.exportStatements',
  'Built for growing teams': 'plan.feature.growingTeams',
  'Calm double-entry workspace': 'plan.feature.doubleEntry',
  'Statements without noise': 'plan.feature.statements',
  'Buy / sell with live rates': 'plan.feature.buySellLive',
  'Buy/sell with vault discipline': 'plan.feature.buySell',
  'Vault and remittance workflows': 'plan.feature.vaultRemittance',
  'Remittances and daily close': 'plan.feature.remittances',
  'Audit-friendly daily close': 'plan.feature.auditDailyClose',
  'Audit-friendly exchange ops': 'plan.feature.auditExchange',
  'Multi-language counter UI': 'plan.feature.multiLangCounter',
  'Catalog and order operations': 'plan.feature.catalogOrders',
  'Image-ready product pages': 'plan.feature.imagePages',
  'Image-ready product catalog': 'plan.feature.imageCatalog',
  'Role-aware store management': 'plan.feature.roleStore',
  'Designed for regional retail': 'plan.feature.designedRetail',
  'Orders and store operations': 'plan.feature.ordersOps',
  'Built for regional retail': 'plan.feature.regionalRetail',
  'Live scores and match pulse': 'plan.feature.liveScores',
  'Fan notifications that matter': 'plan.feature.fanNotifications',
  'Calm sports atmosphere UI': 'plan.feature.sportsUi',
  'Mobile-first experience': 'plan.feature.mobileFirst',
  'Private distribution for your software': 'plan.feature.privateDist',
  'Licenses, devices, and releases': 'plan.feature.licensesDevices',
  'Organization-ready identity': 'plan.feature.orgIdentity',
  'Built for the BeCoder ecosystem': 'plan.feature.ecosystem',

  // Categories (short catalog labels)
  'Point of Sale': 'plan.category.pos',
  Finance: 'plan.category.finance',
  Exchange: 'plan.category.exchange',
  Commerce: 'plan.category.commerce',
  Sports: 'plan.category.sports',
  Platform: 'plan.category.platform',
  Application: 'plan.category.application',
};

function translateKey(key: string, t: Translate, fallback: string): string {
  const translated = t(key);
  return translated === key ? fallback : translated;
}

export function localizeKnownCopy(text: string | undefined | null, t: Translate): string {
  if (!text) return '';
  const trimmed = text.trim();
  const key = KNOWN_COPY[trimmed];
  if (key) return translateKey(key, t, text);

  const coreAccess = /^Core (.+) access$/i.exec(trimmed);
  if (coreAccess) {
    const productName = localizeKnownCopy(coreAccess[1], t) || coreAccess[1];
    const translated = t('plan.feature.coreAccess', { name: productName });
    return translated === 'plan.feature.coreAccess'
      ? `Core ${productName} access`
      : translated;
  }

  return text;
}

export function localizePlanName(
  name: string | undefined | null,
  code: string | undefined | null,
  t: Translate
): string {
  if (code) {
    const byCode = `plan.name.${String(code).toLowerCase()}`;
    const translated = t(byCode);
    if (translated !== byCode) return translated;
  }
  return localizeKnownCopy(name, t) || name || '—';
}

export function localizePlanPeriod(period: string | undefined | null, t: Translate): string {
  if (!period) return '';
  return localizeKnownCopy(period, t);
}

export function localizePlanFeature(feature: string | undefined | null, t: Translate): string {
  return localizeKnownCopy(feature, t);
}

export function localizeStatus(status: string | undefined | null, t: Translate): string {
  if (!status) return '—';
  const key = `status.${status.toLowerCase().replace(/\s+/g, '_')}`;
  const translated = t(key);
  if (translated !== key) return translated;
  const productKey = `product.status.${status}`;
  const productTranslated = t(productKey, status.replace(/_/g, ' '));
  return productTranslated;
}

export function localizeUpdatePolicy(policy: string | undefined | null, t: Translate): string {
  if (!policy) return '—';
  if (policy === 'optional') return t('release.policy.optional');
  if (policy === 'mandatory') return t('release.policy.mandatory');
  return policy;
}

export function localizeProvider(provider: string | undefined | null, t: Translate): string {
  if (!provider) return '—';
  const key = `portal.provider.${provider.toLowerCase()}`;
  const translated = t(key);
  return translated === key ? provider : translated;
}
